'use client';

import type React from 'react';
import { createPortal } from 'react-dom';
import { Hash, ImagePlus, Trash2, Plus } from 'lucide-react';
import { Z } from '@/lib/z-index';

const MOCK_TRENDING_TAGS = [
	'量化策略',
	'AI写作',
	'代码审查',
	'自动化办公',
	'GPT技巧',
	'Agent实战',
	'数据分析',
	'效率工具',
	'Prompt工程',
	'多Agent协作',
	'知识管理',
	'副业赚钱',
];

/** Accepted cover image types */
const COVER_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

interface PublishFormPanelProps {
	// Cover
	coverPreview: string | null;
	coverShrinking: boolean;
	coverError: string;
	coverInputRef: React.RefObject<HTMLInputElement>;
	onCoverInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onCoverRemove: () => void;

	// Title & description
	title: string;
	description: string;
	onTitleChange: (value: string) => void;
	onDescriptionChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	onTextareaKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	textareaRef: React.RefObject<HTMLTextAreaElement>;

	// Hashtag autocomplete
	showHashtagMenu: boolean;
	hashtagMenuIndex: number;
	hashtagMenuPosition: { top: number; left: number };
	hashtagMenuRef: React.RefObject<HTMLDivElement>;
	filteredSuggestions: string[];
	onInsertHashtag: (tag: string) => void;

	// Tags
	visibleTags: string[];
	moreTags: string[];
	showAllTags: boolean;
	tagsMoreRef: React.RefObject<HTMLDivElement>;
	tagsRowRef: React.RefObject<HTMLDivElement>;
	tagMeasureRef: React.RefObject<HTMLDivElement>;
	onTrendingTagClick: (tag: string) => void;
	onToggleShowAllTags: () => void;
	morePopupPos: { top: number; left: number } | null;
	morePopupRef: React.RefObject<HTMLDivElement>;

	// Submit
	submitError: string | null;
}

export function PublishFormPanel({
	coverPreview,
	coverShrinking,
	coverError,
	coverInputRef,
	onCoverInputChange,
	onCoverRemove,
	title,
	description,
	onTitleChange,
	onDescriptionChange,
	onTextareaKeyDown,
	textareaRef,
	showHashtagMenu,
	hashtagMenuIndex,
	hashtagMenuPosition,
	hashtagMenuRef,
	filteredSuggestions,
	onInsertHashtag,
	visibleTags,
	moreTags,
	showAllTags,
	tagsMoreRef,
	tagsRowRef,
	tagMeasureRef,
	onTrendingTagClick,
	onToggleShowAllTags,
	morePopupPos,
	morePopupRef,

	submitError,
}: PublishFormPanelProps) {
	return (
		<div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden relative">
			{/* Board picker overlay */}

			{/* ── Cover upload area ── */}
			{!coverPreview && (
				<button
					className="mx-4 mt-4 flex flex-col items-center justify-center gap-2 py-8 rounded-xl bg-foreground/[0.02] border border-dashed border-border/20 hover:border-primary/30 hover:bg-foreground/[0.04] transition-all group cursor-pointer w-[calc(100%-2rem)]"
					onClick={() => coverInputRef.current?.click()}
					type="button"
				>
					<Plus className="h-5 w-5 text-foreground/20 group-hover:text-primary/50 transition-colors" />
					<span className="text-caption text-foreground/30 group-hover:text-foreground/50 transition-colors">添加封面图，获得额外曝光推荐</span>
				</button>
			)}

			{/* Cover image preview (only shown when has cover) */}
			{coverPreview && (
				<div className={`mx-4 mt-4 ${coverShrinking ? 'animate-fade-out-shrink' : ''}`}>
					<div className="relative group rounded-xl overflow-hidden">
						<img alt="封面预览" className="w-full h-auto max-h-[290px] rounded-xl object-cover animate-fade-in-up" src={coverPreview} />
						<div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2.5 opacity-0 group-hover:opacity-100">
							<button
								className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-white/90 text-gray-800 text-caption font-medium hover:bg-white transition-colors backdrop-blur-sm"
								onClick={() => coverInputRef.current?.click()}
								type="button"
							>
								<ImagePlus className="h-3 w-3" />
								更换
							</button>
							<button
								className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-red-500/90 text-white text-caption font-medium hover:bg-red-500 transition-colors backdrop-blur-sm"
								onClick={onCoverRemove}
								type="button"
							>
								<Trash2 className="h-3 w-3" />
								移除
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Hidden file input */}
			<input accept={COVER_ACCEPTED_TYPES.join(',')} className="hidden" onChange={onCoverInputChange} ref={coverInputRef} type="file" />

			{/* Cover error */}
			{coverError && <p className="text-caption text-destructive px-4 pb-2">{coverError}</p>}

			{/* ── 内容 card ── */}
			<div className="mx-4 mt-3 rounded-xl bg-foreground/[0.02] border border-border/10 overflow-hidden">
				{/* Title */}
				<div className="px-4 pt-3 pb-1">
					<input
						className="w-full bg-transparent text-base font-bold text-foreground placeholder:text-muted-foreground/30 focus:outline-none border-none leading-snug"
						maxLength={100}
						onChange={(e) => onTitleChange(e.target.value)}
						placeholder="输入标题..."
						type="text"
						value={title}
					/>
					<div className="flex items-center justify-between mt-1">
						<div className="flex-1 h-px bg-gradient-to-r from-border/30 via-border/15 to-transparent" />
						<span
							className={`text-micro tabular-nums ml-2 transition-colors ${
								title.length > 90 ? 'text-destructive' : title.length > 0 ? 'text-muted-foreground/40' : 'text-transparent'
							}`}
						>
							{title.length}/100
						</span>
					</div>
				</div>

				{/* Content textarea */}
				<div className="px-4 pb-2">
					<div className="relative">
						<textarea
							className="w-full bg-transparent text-sm text-foreground leading-relaxed placeholder:text-muted-foreground/30 focus:outline-none resize-none border-none font-mono"
							maxLength={300}
							onChange={onDescriptionChange}
							onKeyDown={onTextareaKeyDown}
							placeholder="写点什么吧... 支持 Markdown 语法，输入 # 可快速插入话题标签"
							ref={textareaRef}
							rows={10}
							style={{ caretColor: 'oklch(var(--primary))' }}
							value={description}
						/>

						{/* ── # Autocomplete dropdown ── */}
						{showHashtagMenu && filteredSuggestions.length > 0 && (
							<div
								className="absolute rounded-xl border border-border/40 bg-popover/95 backdrop-blur-xl shadow-xl py-1 min-w-[180px] max-w-[220px] overflow-hidden"
								ref={hashtagMenuRef}
								style={{
									top: hashtagMenuPosition.top,
									left: Math.min(hashtagMenuPosition.left, 400),
									zIndex: 10,
								}}
							>
								<div className="px-2.5 py-1 text-micro text-muted-foreground/50 font-medium">热门话题</div>
								{filteredSuggestions.map((tag, i) => (
									<button
										className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left transition-colors ${
											i === hashtagMenuIndex ? 'bg-primary/10 text-primary' : 'text-foreground/80 hover:bg-secondary/60'
										}`}
										key={tag}
										onMouseDown={(e) => {
											e.preventDefault();
											onInsertHashtag(tag);
										}}
										type="button"
									>
										<Hash className="h-3 w-3 shrink-0 text-muted-foreground/50" />
										<span>{tag}</span>
									</button>
								))}
							</div>
						)}
					</div>
				</div>

				{/* Character count */}
				<div className="px-4 pb-2">
					<span
						className={`text-micro tabular-nums transition-colors ${
							description.length > 290 ? 'text-destructive' : description.length > 260 ? 'text-chart-5' : 'text-muted-foreground/40'
						}`}
					>
						{description.length}/300
					</span>
				</div>

				{/* Tags row */}
				<div className="px-4 pb-4">
					{/* Hidden measurement row — all tags rendered once for width measurement */}
					<div aria-hidden="true" className="flex items-center gap-1.5 absolute invisible pointer-events-none" ref={tagMeasureRef}>
						{MOCK_TRENDING_TAGS.map((tag) => (
							<button className="shrink-0 px-2 py-0.5 rounded-full text-caption border border-transparent" data-tag-btn key={`m-${tag}`} type="button">
								#{tag}
							</button>
						))}
					</div>
					{/* Visible tags row */}
					<div className="flex items-center gap-1.5" ref={tagsMoreRef}>
						<div className="flex items-center gap-1.5 overflow-hidden flex-1 min-w-0" ref={tagsRowRef}>
							{visibleTags.map((tag) => (
								<button
									className={`shrink-0 px-2 py-0.5 rounded-full text-caption transition-colors active:scale-95 border h-6 ${
										description.includes(`#${tag}`)
											? 'bg-primary/10 text-primary border-primary/20'
											: 'bg-foreground/[0.04] text-foreground/60 hover:text-primary hover:bg-primary/10 border-transparent hover:border-primary/20'
									}`}
									key={tag}
									onClick={() => onTrendingTagClick(tag)}
									type="button"
								>
									#{tag}
								</button>
							))}
						</div>
						{moreTags.length > 0 && (
							<button
								className={`shrink-0 px-2 py-0.5 rounded-full text-caption transition-colors active:scale-95 border h-6 ${
									showAllTags
										? 'bg-primary/10 text-primary border-primary/20'
										: 'bg-foreground/[0.04] text-foreground/60 hover:text-primary hover:bg-primary/10 border-transparent hover:border-primary/20'
								}`}
								onClick={onToggleShowAllTags}
								type="button"
							>
								#更多…
							</button>
						)}
					</div>
				</div>
			</div>
			<div className="h-4" />
			{/* Submit error */}
			{submitError && <p className="text-caption text-red-500 text-right px-5 pb-2">{submitError}</p>}

			{/* ═══════ More tags popup (portal to body) ═══════ */}
			{showAllTags &&
				morePopupPos &&
				createPortal(
					<div
						className="fixed rounded-xl border border-border/30 bg-popover/95 backdrop-blur-xl shadow-xl py-2 px-2"
						ref={morePopupRef}
						style={{
							top: morePopupPos.top,
							left: morePopupPos.left,
							minWidth: 200,
							zIndex: Z.MODAL_CONTENT + 5,
						}}
					>
						<div className="grid grid-cols-3 gap-1.5">
							{moreTags.map((tag) => (
								<button
									className={`px-2 py-0.5 rounded-full text-caption transition-colors active:scale-95 border h-6 ${
										description.includes(`#${tag}`)
											? 'bg-primary/10 text-primary border-primary/20'
											: 'bg-foreground/[0.04] text-foreground/60 hover:text-primary hover:bg-primary/10 border-transparent hover:border-primary/20'
									}`}
									key={tag}
									onClick={() => {
										onTrendingTagClick(tag);
										onToggleShowAllTags();
									}}
									type="button"
								>
									#{tag}
								</button>
							))}
						</div>
					</div>,
					document.body,
				)}
		</div>
	);
}
